import { t } from "i18next";
import ReactEcharts from "echarts-for-react";
import ReactDomServer from "react-dom/server";
import _, { isUndefined } from "lodash";
import { DateTime, Duration, Interval } from "luxon";
import { useState } from "react";
import { TbChevronDown, TbChevronUp } from "react-icons/tb";
import { useAppStore } from "../../../../../stores/RootStore";
import { DataRow } from "../../../../../stores/TabsStore";
import { DateShift, Event, FShift } from "../../../../../types/types";
import { HourHeatMap } from "./HourHeatMap";
import TimelineChart from "./TimelineChart";
import { calculateInteractions } from "../../../../../lib/calculations";
import { hexColor } from "../../../../../enums/colors";
import { stringToColour } from "../../../../../lib/utils";

export function EquipmentInteractions({data}:{data: DataRow[]}) {
  let grouppedByDay = _.groupBy(_.orderBy(data, el => el.dateShift.start_day, "desc") , el => el.dateShift.start_day)

  return (
    <div className="flex flex-col space-y-2 my-6">
      <div className="grid grid-cols-12 text-sm px-4">
        <div className="col-span-3 flex space-x-2"><div className="font-semibold">{t('date')}</div></div>
        <div className="col-span-3 flex">{t('interaction')}</div>
        <div className="col-span-1 flex justify-end">{t('worked')}</div>
        <div className="col-span-1 flex justify-end">{t('stopped')}</div>
        <div className="col-span-2 flex justify-end">{t('maintenance')}</div>
        <div className="col-span-1 flex justify-end">{t('repair')}</div>
        <div className="col-span-1 flex justify-end">{t('service')}</div>
      </div>
      {grouppedByDay ? Object.entries(grouppedByDay).map(([day, dayData]) => {
        let ordered = _.orderBy(dayData, el => el.dateShift.start_time)
        let duration = _.sum(ordered.map(r => r.availability.on.seconds)) + _.sum(ordered.map(r => r.unavailability.on.seconds))
        return (
          <div key={day} className="border flex flex-col rounded text-sm">
            <div className="w-full flex flex-col">
              <div className="grid grid-cols-12 px-4 py-2">
                <div className="col-span-3 font-semibold">{ordered[0].dateShift.start_day.toFormat("dd.MM.yyyy")} ({ordered[0].dateShift.start_day.weekdayLong})</div>
                <div className="col-span-3">{Duration.fromObject({seconds: duration }).toFormat("hh:mm:ss")}</div>
                <div className="col-span-1 flex justify-end space-x-2">
                  <span>{Duration.fromObject({seconds: _.sum(ordered.map(r => r.availability.worked.seconds))}).toFormat("hh:mm:ss")}</span>
                  <span className="font-medium">{((_.sum(ordered.map(r => r.availability.worked.seconds)) / duration) * 100).toFixed(1)}%</span>
                </div>
                <div className="col-span-1 flex justify-end space-x-2">
                  <span>{Duration.fromObject({seconds: _.sum(ordered.map(r => r.availability.stopped.seconds))}).toFormat("hh:mm:ss")}</span>
                  <span className="font-medium">{((_.sum(ordered.map(r => r.availability.stopped.seconds)) / duration) * 100).toFixed(1)}%</span>
                </div>
                <div className="col-span-2 flex justify-end space-x-2">
                  <span>{Duration.fromObject({seconds: _.sum(ordered.map(r => r.maintenance.on.seconds))}).toFormat("hh:mm:ss")}</span>
                  <span className="font-medium">{((_.sum(ordered.map(r => r.maintenance.on.seconds)) / duration) * 100).toFixed(1)}%</span>
                </div>
                <div className="col-span-1 flex justify-end space-x-2">
                  <span>{Duration.fromObject({seconds: _.sum(ordered.map(r => r.repair.on.seconds))}).toFormat("hh:mm:ss")}</span>
                  <span className="font-medium">{((_.sum(ordered.map(r => r.repair.on.seconds)) / duration) * 100).toFixed(1)}%</span>
                </div>
                <div className="col-span-1 flex justify-end space-x-2">
                  <span>{Duration.fromObject({seconds: _.sum(ordered.map(r => r.service.on.seconds))}).toFormat("hh:mm:ss")}</span>
                  <span className="font-medium">{((_.sum(ordered.map(r => r.service.on.seconds)) / duration) * 100).toFixed(1)}%</span>
                </div>
              </div>
              {dayData.map((r, i) => <DayEquipmentInteraction key={i} data={r!}/>)}
              
            </div>
          </div>
        )
        })
        :
        <div>{t('no_data')}</div>
      }
    </div>
  )
}


function DayEquipmentInteraction({data}:{data: DataRow}) {
  const shift = useAppStore().shifts.find(s => s.id === data.dateShift.id) as unknown as FShift
  const [open, setOpen] = useState(false)

  return (
    <div className="px-4 py-2 bg-muted border-border border-b last:border-0">
      <div className="flex flex-col space-y-2" >
        <div className="grid grid-cols-12">
          <div className="col-span-4 flex cursor-pointer items-center space-x-2 hover:text-primary truncate" onClick={() => setOpen(!open)}>
            {open ? <TbChevronUp className="w-6" /> : <TbChevronDown className="w-6"/>}
            <div className="font-semibold truncate">{data.person?.attributes.last_name} {String(data.person?.attributes.first_name).charAt(0).toUpperCase()}. {String(data.person?.attributes.middle_name).charAt(0).toUpperCase()}. ({data.equipment.attributes.name})</div>
          </div>
          <div className="col-span-2 font-semibold">{data.dateShift.label} ({t(data.person?.attributes.access_tokens.find(c => c.id === data.interactionIds[0])?.access_token_type!)})</div>
          <div className="col-span-1 flex justify-end">{data.works.worked.time}</div>
          <div className="col-span-1 flex justify-end">{data.works.stopped.time}</div>
          <div className="col-span-2 flex justify-end">{data.maintenance.on.time}</div>
          <div className="col-span-1 flex justify-end">{data.repair.on.time}</div>
          <div className="col-span-1 flex justify-end">{data.service.on.time}</div>
        </div>
        {open ? 
          <div className="space-y-2 h-20">
            <>
              <InteractionPersonTimeline events={data.events!} dateShift={data.dateShift}/>
              <TimelineChart events={data.events!} personId={Number(data.person?.id)} from={data.dateShift.start_time} to={data.dateShift.end_time} shift={shift} height={16} width="100%"/>
              <HourHeatMap events={data.events!} personId={Number(data.person?.id)} from={data.dateShift.start_time} to={data.dateShift.end_time} shift={shift} height={40} width="100%"/>
            </>
          </div> : <></>
        }
      </div>
    </div>
  )
}

function InteractionPersonTimeline({events, dateShift}: {events: Event[]; dateShift: DateShift}) {
  const {persons} = useAppStore()
  let interactions = calculateInteractions(events, dateShift)
  let personIds = _.uniq(interactions.map(i => i.personnel_id))
  const start = interactions && interactions.length > 0 ? DateTime.fromSQL(String(interactions[0].from)) : dateShift.start_day 
  const end = interactions && interactions.length > 0 ? DateTime.fromSQL(String(interactions[interactions.length - 1].to)) : dateShift.end_day 

  const personsConfig: {[id: string]:{name: string, color: string}} = {0: {name: "none", color: hexColor.border}}

  personIds.map(pid => {
    if(pid === 0) {
      personsConfig[Number(pid)] = {name: t('without_person'), color: "rgba(128, 128, 128, 0.5)"}
    } else {
      let person = persons?.find(e => e.id === String(pid))!
      let name = `${person.attributes.last_name!} ${person.attributes.first_name!} ${person.attributes.middle_name!}`
      if(isUndefined(person) || !person) {
        personsConfig[Number(pid)] = {name: t('unknown_person'), color: "rgba(128, 128, 128, 0.5)"}
      } else {
        personsConfig[Number(pid)] = {name: name, color: stringToColour(name)}
      }
      
    }
  })

  const intervals = [
    Interval.fromDateTimes(start, end)
      .splitBy({ hour: 1 })
      .map((d) => d.start?.toISOTime({ includeOffset: false, suppressMilliseconds: true })),
    end.toISOTime({ includeOffset: false, suppressMilliseconds: true }),
  ].flat();

  const shiftDuration = end.toSeconds() - start.toSeconds();

  let graphData: any[] = []
  let style = {
      borderRadius: [50, 50, 50, 50], // Specify the border radius
      borderWidth: 0,
      borderType: 'solid',
      shadowBlur: 0,
      shadowOffsetX: 0,
      shadowOffsetY: 0
  }
  
  interactions.map((int, idx) => {
    graphData.push({
      name: personsConfig[Number(int.personnel_id)]?.name,
      type: "bar",
      stack: "timeline",
      color: personsConfig[Number(int.personnel_id)]?.color,
      data: [int.duration],
      itemStyle: style,
    })
  })
    
  const getOptions = () => ({
    tooltip: {
      trigger: "item",
      borderWidth: 0,
      padding: 0,
      axisPointer: {
        type: "shadow",
      },
      formatter: function (params: {name: any; seriesName: string; value: (number)[] }) {
        return ReactDomServer.renderToStaticMarkup(<Tooltip params={params} />);
      }
    },
    grid: {
      left: "0%",
      right: "0%",
      bottom: -5,
      top: -5,
      containLabel: false,
    },
    animation: false,
    xAxis: [
      { show: false, max: shiftDuration },
      { show: false, data: intervals },
    ],
    yAxis: { show: false, type: "category" },
    series: graphData,
  });
  
  return interactions.length > 0 && interactions ? <ReactEcharts option={getOptions()} style={{ height: 5, width: "100%" }} opts={{ renderer: "canvas" }} /> : <></>;
}

const Tooltip = ({ params }: { params: { name: any; seriesName: string; value: (number)[] } }) => {
  return (
    <div className="relative bg-background text-foreground text-sm p-2">
      <div className="font-medium pb-1">{params.seriesName}</div>
      <div className="flex flex-col gap-1">{Duration.fromObject({seconds: Number(params.value)}).toFormat("hh:mm:ss")}</div>
    </div>
  );
};
